import { useEffect, useState } from 'react';

/**
 * Fetches /api/config/public once per app lifetime and caches it in-memory.
 *
 * Currently exposes:
 *   - barion_pixel_id
 *   - payments_enabled (bool)
 *   - payments_disabled_message (string)
 *
 * Returns { config, loading }. `config` is `null` while loading, then the raw
 * server payload. If the request fails we assume payments ENABLED so we never
 * block real customers on a transient network hiccup.
 */
let _cache = null;
let _inflight = null;

const fetchConfig = () => {
  if (_cache) return Promise.resolve(_cache);
  if (_inflight) return _inflight;
  const base = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/$/, '');
  _inflight = fetch(`${base}/api/config/public`, { credentials: 'omit' })
    .then((r) => (r.ok ? r.json() : null))
    .then((data) => {
      _cache = data || { payments_enabled: true };
      _inflight = null;
      return _cache;
    })
    .catch(() => {
      _cache = { payments_enabled: true };
      _inflight = null;
      return _cache;
    });
  return _inflight;
};

export const usePublicConfig = () => {
  const [config, setConfig] = useState(_cache);
  const [loading, setLoading] = useState(!_cache);
  useEffect(() => {
    let alive = true;
    if (_cache) return;
    fetchConfig().then((data) => {
      if (!alive) return;
      setConfig(data);
      setLoading(false);
    });
    return () => { alive = false; };
  }, []);
  return { config, loading };
};

/**
 * Fizetős állapotfelmérés kapcsoló. Alapértelmezés: FALSE → a felmérés
 * ingyenes, a 10.000 Ft-os díj sehol nem jelenik meg. Adminból kapcsolható:
 * /admin/beallitasok.
 */
export const usePaidAssessment = () => {
  const { config } = usePublicConfig();
  return !!config?.paid_assessment;
};

export default usePublicConfig;
